// Portal V2 inline player — renders a course without iframe.
// Data comes from the M3 data.json endpoint; LMS tracking via PortalProvider.
import { config } from '_appConfig'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { matchPath, useHistory, useLocation, useParams } from 'react-router-dom'

import Course from 'components/Course'
import {
  getPublicPortalCourse,
  getSessionById,
  type IPublicPortalCourseData,
} from 'components/contentspace/api'
import {
  clearPortalSession,
  clearSessionCookie,
  getPortalAuthCookie,
  identityKeyFromAuth,
} from 'components/contentspace/sessionCookie'
import type { IContentSpaceSession, SessionResumeMode } from 'components/contentspace/types'
import { PORTAL_V2_COURSE_MATCH, PORTAL_V2_PATHS, type PortalV2CoursePath } from 'constants/paths'
import { projectsDeepGetSetCache } from 'gql/projects/apollo'
import PlayerHeader from 'routes/portal/ContentSpacePublic/components/PlayerHeader'
import UserMenu from 'routes/portal/ContentSpacePublic/components/UserMenu/UserMenu'
import { CompletionDialog } from 'routes/portal/ContentSpacePublic/pages/PortalLms/components/CompletionDialog/CompletionDialog'
import { SessionResume } from 'routes/portal/ContentSpacePublic/pages/PortalLms/components/SessionResume/SessionResume'
import { GlobalPortalLoader } from 'routes/portal/components/GlobalPortalLoader'
import type { WindowDefaultData } from 'services/ScormData/ScormData'
import ProjectProvider from 'services/Store/Project'
import { PreviewMode } from 'services/Store/Project/enums'
import { prepareDeepProject } from 'services/Store/Project/prepareDeepProject'
import type { ScormSettings } from 'services/Store/Project/types'
import { LmsService } from 'services/lms/lms.service'
import type { ISessionPayload } from 'services/lms/providers/portal.provider'
import { FontsInjector } from 'utils/font/injectors'
import { parsePako } from 'utils/gzip'
import { testPropsEl } from 'utils/test/qaData'

import { usePortalParams } from './ContentSpacePublic/hooks/usePortalParams'
import { getOrCreatePortalSession } from './session-helpers'

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | {
      status: 'awaitingResumeChoice'
      existingSession: IContentSpaceSession
      courseName: string
      portalLandingUrl: string
    }
  | {
      status: 'ready'
      session: IContentSpaceSession
      defaultData: WindowDefaultData
      courseName: string
      portalLandingUrl: string
      hideHeader: boolean
    }

type CompletionState = {
  session: IContentSpaceSession
} | null

interface IPortalV2Session {
  sessionId: string
  sessionToken: string
  cmiData: Record<string, string>
  endpoint: string
  email?: string
  userName?: string
  onTerminated?: (payload: ISessionPayload) => void
  onCommit?: (payload: ISessionPayload) => void
}

type PortalV2Window = Window &
  typeof globalThis & {
    defaultData?: WindowDefaultData
    __PORTAL_V2__?: boolean
    __PORTAL_V2_SESSION__?: IPortalV2Session
  }

const buildCompletionSession = (
  base: IContentSpaceSession,
  payload: ISessionPayload,
): IContentSpaceSession => ({
  ...base,
  status: payload.status ?? base.status,
  score: payload.score ?? base.score,
  scoreMin: payload.scoreMin ?? base.scoreMin,
  scoreMax: payload.scoreMax ?? base.scoreMax,
  progress: payload.progress ?? base.progress,
  location: payload.location ?? base.location,
})

const resolveEffectiveResumeMode = (
  coursePortal: IPublicPortalCourseData['coursePortal'],
  courseId: string,
): SessionResumeMode => {
  // Start course always resumes silently — matches V1 PublicPlayer behaviour.
  if (coursePortal.startCourseId === courseId) {
    return 'ALWAYS_RESUME'
  }
  return coursePortal.sessionResumeMode ?? 'ASK'
}

const SOFT_TIMEOUT_MS = 3000
const HARD_TIMEOUT_MS = 15000

// Select the canonical PORTAL_V2_PATHS variant that matches the current URL so
// ProjectProvider's usePathParams can extract courseId/sectionId/blockId. Non-
// canonical URLs never reach this component (Route in portal.tsx is exact over
// PORTAL_V2_COURSE_MATCH); falls back to `course` just to keep the return type
// total.
const useCanonicalPortalV2Path = (): PortalV2CoursePath => {
  const { pathname } = useLocation()
  return useMemo(() => {
    for (const candidate of PORTAL_V2_COURSE_MATCH) {
      if (matchPath(pathname, { path: candidate, exact: true })) {
        return candidate
      }
    }
    return PORTAL_V2_PATHS.course
  }, [pathname])
}

const PortalV2Player: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>()
  const { companyId, portalId } = usePortalParams()
  const canonicalPath = useCanonicalPortalV2Path()
  const history = useHistory()
  const [loadState, setLoadState] = useState<LoadState>({ status: 'loading' })
  const [completion, setCompletion] = useState<CompletionState>(null)
  const [resumeChoice, setResumeChoice] = useState<'continue' | 'startNew' | null>(null)
  const [loadingElapsed, setLoadingElapsed] = useState(0)
  // Hold the latest ready-session ref so the terminate-callback (fired from
  // LmsService, outside React render) can read up-to-date data.
  const sessionRef = useRef<IContentSpaceSession | null>(null)
  // Gate async callbacks against unmount: LmsService fires `terminate` outside
  // React — without this guard it can setState on an unmounted component.
  const mountedRef = useRef(true)
  useEffect(
    () => () => {
      mountedRef.current = false
    },
    [],
  )
  // Mirror of loadState.status for interval/timeout callbacks that capture
  // state by closure — lets them skip late setState after status transitions.
  const loadStateStatusRef = useRef(loadState.status)
  useEffect(() => {
    loadStateStatusRef.current = loadState.status
  }, [loadState.status])

  // Tick elapsed time while loading so we can show a soft "still loading"
  // message after SOFT_TIMEOUT_MS and fall through to error after HARD_TIMEOUT_MS
  // if load() silently stalls.
  useEffect(() => {
    if (loadState.status !== 'loading') {
      return
    }
    const mountedAt = performance.now()
    setLoadingElapsed(0)
    const id = window.setInterval(() => {
      // Guard against a callback scheduled before load() transitioned
      // `loading → ready` — without it, a late tick overwrites the ready
      // state with `error`, flashing "Failed to load" on a successful load.
      if (loadStateStatusRef.current !== 'loading') {
        return
      }
      const elapsed = performance.now() - mountedAt
      setLoadingElapsed(elapsed)
      if (elapsed >= HARD_TIMEOUT_MS) {
        setLoadState({
          status: 'error',
          message: 'The course is taking longer than expected to load.',
        })
      }
    }, 500)
    return () => window.clearInterval(id)
  }, [loadState.status])

  useEffect(() => {
    let cancelled = false
    const win = window as PortalV2Window

    const load = async () => {
      try {
        const endpoint = config.io.contentspaceEndpoint

        // 1. Portal+course metadata — gives us sessionResumeMode, course name, etc.
        const portalCourse = await getPublicPortalCourse(companyId, portalId, courseId)
        // `portalLandingUrl` is consumed by <Link to={…}>. The Router in portal.tsx
        // already has basename `/contentspace/public/portal/<id>` (see getPortalV2Basename),
        // so passing the absolute prefix would double-stack it. `/` resolves to the
        // landing under the current basename, and stays correct on custom domains
        // where the basename is empty.
        const portalLandingUrl = '/'
        const courseName = portalCourse.name
        const resumeMode = resolveEffectiveResumeMode(portalCourse.coursePortal, courseId)

        // 2. Get or resume session. `forceNew` — only when resumeChoice='startNew'
        //    or resumeMode='ALWAYS_RESET'.
        const forceNew = resumeChoice === 'startNew' || resumeMode === 'ALWAYS_RESET'
        const {
          session: shellSession,
          sessionToken,
          isNew,
        } = await getOrCreatePortalSession(companyId, portalId, courseId, { forceNew })

        // V1 parity: a fresh session must start the course at the first landing
        // (block 0 of section 0). In V1 the iframe owns course position internally,
        // so the outer URL is irrelevant. In V2 the URL *is* the position — a
        // deep-linked /sections/X/blocks/Y survives reload and would override the
        // reset. Strip it here so ProjectProvider falls back to the first block.
        if (forceNew && isNew) {
          history.replace(`/courses/${courseId}`)
        }

        // 3. ASK mode + existing session + no choice yet → show SessionResume modal.
        if (!isNew && resumeMode === 'ASK' && resumeChoice === null) {
          // Hydrate so the modal can show progress/score/lastAccessAt.
          const hydrated = await getSessionById(shellSession.id, sessionToken)
          if (cancelled) {
            return
          }
          setLoadState({
            status: 'awaitingResumeChoice',
            existingSession: hydrated.session,
            courseName,
            portalLandingUrl,
          })
          return
        }

        // 4. Hydrate full cmiData when resuming
        let session = shellSession
        if (!isNew) {
          const res = await getSessionById(shellSession.id, sessionToken)
          session = res.session
        }

        // 5. Fetch course data from M3 endpoint
        const dataRes = await fetch(
          `${endpoint}/public/portal/${portalId}/course/${courseId}/data.json`,
          { credentials: 'include' },
        )
        if (!dataRes.ok) {
          throw new Error(`Failed to load course data: ${dataRes.status}`)
        }
        const rawData = await dataRes.json()

        if (cancelled) {
          return
        }

        // 6. Expose session to PortalProvider via window globals BEFORE LmsService.initialize.
        // Also expose `window.defaultData` — Course internals call `getWindowData()`
        // synchronously during render; without it they throw "No window default data".
        const cmiData = (session.cmiData as Record<string, string> | null) ?? {}
        win.defaultData = rawData

        // Populate Apollo cache with project file metadata so Course elements (images,
        // logos, etc.) can resolve file paths — same as routes/scorm/index.tsx does at
        // bundle init for the iframe SCORM player.
        const cd = rawData.compressedData
        const pd: WindowDefaultData['data'] | undefined = cd
          ? parsePako<WindowDefaultData['data']>(cd)
          : rawData.data
        const windowProject = pd?.projects?.default?.project
        if (windowProject) {
          projectsDeepGetSetCache(windowProject)
        }

        sessionRef.current = session

        win.__PORTAL_V2__ = true
        win.__PORTAL_V2_SESSION__ = {
          sessionId: session.id,
          sessionToken: sessionToken,
          // BUG: xAPI admin log was empty because LearningLocker statements
          // were sent with `actor.mbox = anonymous@example.com` (LmsService
          // fallback when learnerEmail is missing). Backend `LrsService`
          // filters LRS by `agent.mbox = mailto:<session.email>`, so it never
          // matched. Propagating email/userName here lets PortalProvider seed
          // learnerInfo, which feeds the slave XapiProvider's actor.
          email: session.email ?? undefined,
          userName: session.userName ?? undefined,
          cmiData,
          endpoint,
          onTerminated: (payload: ISessionPayload) => {
            // `cancelled` guards against a stale callback fired by the previous
            // effect's PortalProvider after Start New / ALWAYS_RESET swaps the
            // session — without it, a late flush of the old session's payload
            // would overwrite the freshly mounted session's progress.
            if (cancelled || !mountedRef.current) {
              return
            }
            const base = sessionRef.current
            if (!base) {
              return
            }
            setCompletion({ session: buildCompletionSession(base, payload) })
          },
          onCommit: (payload: ISessionPayload) => {
            if (cancelled || !mountedRef.current) {
              return
            }
            const base = sessionRef.current
            if (!base) {
              return
            }
            const updated = buildCompletionSession(base, payload)
            sessionRef.current = updated
            setLoadState((prev) => (prev.status === 'ready' ? { ...prev, session: updated } : prev))
          },
        }

        // 7. Initialize LmsService so PortalProvider is ready before course mounts
        await LmsService.instance.initialize()

        if (cancelled) {
          return
        }

        setLoadState({
          status: 'ready',
          session,
          defaultData: rawData as WindowDefaultData,
          courseName,
          portalLandingUrl,
          hideHeader:
            portalCourse.coursePortal.startCourseId === courseId ||
            Boolean(portalCourse.coursePortal.hideHeader),
        })
      } catch (err) {
        if (!cancelled) {
          console.error('[PortalV2Player] Load error:', err)
          setLoadState({ status: 'error', message: String(err) })
        }
      }
    }

    void load()

    return () => {
      cancelled = true
      // Reset the LmsService singleton so re-mounting the player (e.g. after
      // navigating away and back) starts with a fresh PortalProvider seeded
      // from the new session — prevents stale cmiData from a previous session.
      LmsService.reset()
      // Clear per-session window globals. __PORTAL_V2__ stays set for the bundle
      // lifetime (set in src/portal.tsx module init) — it's the bundle marker,
      // not a per-player state, and shared helpers rely on it outside the player.
      delete win.__PORTAL_V2_SESSION__
      delete win.defaultData
    }
  }, [companyId, portalId, courseId, resumeChoice, history])

  const readyDefaultData = loadState.status === 'ready' ? loadState.defaultData : null

  const prepared = useMemo(() => {
    if (!readyDefaultData) {
      return null
    }
    // Decompress course project data (same as ScormData.getWindowData)
    const compressedData = readyDefaultData.compressedData
    const projectData = compressedData
      ? parsePako<WindowDefaultData['data']>(compressedData)
      : readyDefaultData.data
    return prepareDeepProject(projectData?.projects?.default?.project)
  }, [readyDefaultData])

  const scormSettings = useMemo<ScormSettings | undefined>(
    () => readyDefaultData?.settings || undefined,
    [readyDefaultData],
  )

  const handleContinue = useCallback(() => setResumeChoice('continue'), [])
  const handleStartNew = useCallback(() => {
    // Drop the per-identity cookie so getOrCreatePortalSession hits forceNew.
    const identity = identityKeyFromAuth(getPortalAuthCookie(portalId))
    if (identity) {
      clearSessionCookie(portalId, courseId, identity)
    }
    setResumeChoice('startNew')
  }, [portalId, courseId])

  const handleLogout = useCallback(() => {
    clearPortalSession(portalId)
    window.location.href = `/contentspace/public/portal/${portalId}`
  }, [portalId])

  if (loadState.status === 'awaitingResumeChoice') {
    return (
      <SessionResume
        courseName={loadState.courseName}
        onContinue={handleContinue}
        onStartNew={handleStartNew}
        portalLandingUrl={loadState.portalLandingUrl}
        session={loadState.existingSession}
      />
    )
  }

  if (loadState.status === 'loading') {
    const showSoftMessage = loadingElapsed >= SOFT_TIMEOUT_MS
    return (
      <>
        <GlobalPortalLoader reason='player-load' visible />
        {showSoftMessage && (
          <div
            aria-live='polite'
            role='status'
            style={{
              position: 'fixed',
              bottom: 32,
              left: '50%',
              transform: 'translateX(-50%)',
              padding: '10px 16px',
              // Scrim/overlay tokens — intentionally not branded.
              backgroundColor: 'rgba(0,0,0,0.6)',
              borderRadius: 6,
              color: 'var(--brand-lightTextColor)',
              fontSize: 13,
              fontFamily: 'Inter, sans-serif',
              zIndex: 1001,
              pointerEvents: 'none',
            }}
          >
            Still loading…
          </div>
        )}
      </>
    )
  }

  if (loadState.status === 'error') {
    const portalLandingUrl = `/contentspace/public/portal/${portalId}`
    return (
      <div
        aria-live='assertive'
        role='alert'
        style={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 16,
          padding: 24,
          backgroundColor: 'var(--brand-primaryBackgroundColor)',
          color: 'var(--brand-primaryTextColor)',
          fontFamily: 'Inter, sans-serif',
          textAlign: 'center',
        }}
      >
        <h2 style={{ margin: 0, fontWeight: 600, fontSize: 20 }}>Failed to load course</h2>
        <p
          style={{
            margin: 0,
            fontSize: 14,
            color: 'var(--brand-secondaryTextColor)',
            maxWidth: 480,
          }}
        >
          {loadState.message}
        </p>
        <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '8px 20px',
              border: 'none',
              borderRadius: 6,
              backgroundColor: 'var(--brand-primaryColor)',
              color: 'var(--brand-lightTextColor)',
              fontSize: 14,
              cursor: 'pointer',
            }}
            type='button'
          >
            Reload
          </button>
          <button
            onClick={() => {
              window.location.href = portalLandingUrl
            }}
            style={{
              padding: '8px 20px',
              border: '1px solid var(--brand-borderColor)',
              borderRadius: 6,
              backgroundColor: 'var(--brand-primaryBackgroundColor)',
              color: 'var(--brand-primaryTextColor)',
              fontSize: 14,
              cursor: 'pointer',
            }}
            type='button'
          >
            Back to Portal
          </button>
        </div>
      </div>
    )
  }

  if (!prepared?.project) {
    return <GlobalPortalLoader reason='project-prepare' visible />
  }

  const userName = loadState.session.userName || loadState.session.email || undefined

  return (
    // Flex column: PlayerHeader is flex-none, Course below is flex: 1. Course's
    // overlayscrollbars still gets a bounded ancestor via the wrapper in src/portal.tsx
    // (height: 100vh). A data-qa marker lets e2e detect V2 inline player readiness.
    <div
      {...testPropsEl('inlinePlayer', { name: 'portalV2' }, { sessionId: loadState.session.id })}
      style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
    >
      {!loadState.hideHeader && (
        <PlayerHeader
          backRedirectUrl={`/contentspace/public/portal/${portalId}`}
          backUrl={loadState.portalLandingUrl}
          courseName={loadState.courseName}
          isDebug={loadState.session.isDebug}
          progress={loadState.session.progress}
          userName={userName}
        />
      )}
      {loadState.hideHeader && userName && (
        <div style={{ position: 'fixed', top: 8, right: 8, zIndex: 100 }}>
          <UserMenu onLogout={handleLogout}>
            <button
              style={{
                padding: '4px 12px',
                border: 'none',
                borderRadius: 4,
                // Scrim — overlays course canvas, intentionally not branded.
                background: 'rgba(0, 0, 0, 0.5)',
                color: 'var(--brand-lightTextColor)',
                fontSize: 12,
                opacity: 0.4,
                cursor: 'pointer',
              }}
              title='User menu'
              type='button'
            >
              {userName}
            </button>
          </UserMenu>
        </div>
      )}
      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <ProjectProvider
          blocks={prepared.blocks}
          fonts={[]}
          mode={PreviewMode.scorm}
          path={canonicalPath}
          project={prepared.project}
          scormSettings={scormSettings}
          sections={prepared.sections}
        >
          <FontsInjector fontGroups={loadState.defaultData.fontsGroups || []} />
          <Course />
        </ProjectProvider>
      </div>
      {completion && (
        <CompletionDialog
          courseName={loadState.courseName}
          onClose={() => setCompletion(null)}
          portalLandingUrl={loadState.portalLandingUrl}
          session={completion.session}
        />
      )}
    </div>
  )
}

export default PortalV2Player
